import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { AppFooter } from "@/components/layout/AppFooter";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import { motion } from "framer-motion";

export default function ChangePassword() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refreshMe();
      const target = user?.role === "admin" ? "/admin" : user?.role === "parent" ? "/parent" : user?.role === "institute" ? "/institute" : user?.role === "teacher" ? "/teacher" : "/student";
      nav(target);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 flex items-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-[420px] h-[420px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.30), transparent 60%)" }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }} className="relative w-full max-w-md mx-auto px-6 py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <ShieldCheck size={12} /> Change password
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Set a new password.</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">
            {user?.password_change_required
              ? "Your account was set up with a temporary password. Please choose your own now to continue."
              : "Update the password you use to sign in."}
          </p>
          <form onSubmit={submit} className="bg-white border border-black/8 rounded-2xl mt-8 p-6 sm:p-8 space-y-4 shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)]">
            <GlowField label={user?.password_change_required ? "Temporary password" : "Current password"} testid="cp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
            <GlowField label="New password" testid="cp-new" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} />
            <GlowField label="Confirm new password" testid="cp-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            <CtaButton type="submit" variant="primary" size="lg" disabled={loading} className="w-full rounded-full" data-testid="cp-submit">
              <KeyRound size={14} /> {loading ? "Updating…" : "Update password"}
            </CtaButton>
          </form>
        </motion.div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
